import {
  CalendarDays,
  ChevronLeft,
  ChevronDown,
  Clock4,
  MapPin,
  FileText,
  Image as ImageIcon,
  File as FileIcon,
  Download,
  MessageCircle,
  Send,
  RefreshCw,
  Eye,
  MousePointerClick,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export default function EventDetail({ event }) {
  const currentEvent = event ?? null;
  const [collaborationRequests, setCollaborationRequests] = useState([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [showInquiries, setShowInquiries] = useState(false);
  const [inquiryThreads, setInquiryThreads] = useState([]);
  const [isLoadingInquiries, setIsLoadingInquiries] = useState(false);
  const [inquiriesFetched, setInquiriesFetched] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null); // { inquiryId, name }
  const [thread, setThread] = useState([]);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (currentEvent?.id) {
      fetchCollaborationRequests();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.id]);

  // Fetch user services (categories) on component mount
  useEffect(() => {
    const fetchUserServices = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_USER_SERVICES}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const result = await response.json();
          const services = result?.data || result;
          setCategories(Array.isArray(services) ? services : []);
        } else {
          console.error("Failed to fetch user services");
        }
      } catch (error) {
        console.error("Error fetching user services:", error);
      } finally {
      }
    };

    fetchUserServices();
  }, []);

  useEffect(() => {
    if (currentEvent?.coverImageUrl) {
      setIsImageLoading(true);
      setImageError(false);

      // Preload the image to check if it's already cached
      const img = new Image();
      img.src = currentEvent.coverImageUrl;

      if (img.complete) {
        // Image is already loaded (cached)
        setIsImageLoading(false);
      } else {
        // Image needs to load
        img.onload = () => setIsImageLoading(false);
        img.onerror = () => {
          setIsImageLoading(false);
          setImageError(true);
        };
      }
    } else {
      setIsImageLoading(false);
    }
  }, [currentEvent?.coverImageUrl]);

  const fetchCollaborationRequests = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingRequests(true);
    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.NONPROFIT_GET_COLLABORATION_REQUEST_BY_EVENT}/${currentEvent.id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json().catch(() => null);
      const statusCode = result?.statusCode ?? response.status;

      if (response.ok && statusCode < 400 && result?.data) {
        // Extract all recipients with their tasks
        const recipients = result.data.recipients || [];
        setCollaborationRequests(recipients);
      } else {
        // No collaboration request exists for this event, which is fine
        setCollaborationRequests([]);
      }
    } catch (error) {
      console.error("Fetch collaboration requests error:", error);
      // Silently fail - it's okay if there's no collaboration request
      setCollaborationRequests([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Event inquiries currently live in two tables: logged-in PIN threads in the
  // `inquiries` module, anonymous guest threads in `event-inquiries`. Both are
  // fetched and merged here. Fall back to the deviceId when there is no name.
  const inquiryDisplayName = (item) =>
    [item.firstName, item.lastName].filter(Boolean).join(" ") ||
    (item.deviceId ? `Guest · ${item.deviceId}` : "Unknown User");

  const fetchInquiries = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token || !currentEvent?.id) return;

    setIsLoadingInquiries(true);
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    try {
      // Logged-in threads (legacy `inquiries`) + anonymous threads (`event-inquiries`).
      const [legacyRes, guestRes] = await Promise.all([
        fetch(`${BASE_URL}${API_ENDPOINTS.INQUIRIES_BY_EVENT}/${currentEvent.id}`, { headers }),
        fetch(`${BASE_URL}${API_ENDPOINTS.EVENT_INQUIRIES}/${currentEvent.id}`, { headers }),
      ]);
      const legacyJson = await legacyRes.json().catch(() => null);
      const guestJson = await guestRes.json().catch(() => null);

      const legacyThreads = (legacyRes.ok && Array.isArray(legacyJson?.data) ? legacyJson.data : []).map(
        (t) => ({
          isGuest: false,
          threadKey: `u:${t.inquiryId}`,
          inquiryId: t.inquiryId,
          deviceId: null,
          firstName: t.pinUserFirstName,
          lastName: t.pinUserLastName,
          messageCount: t.messageCount,
          lastMessage: t.lastMessage,
          lastMessageAt: t.lastMessageAt,
        }),
      );

      // Only anonymous (device) threads from event-inquiries; logged-in threads come
      // from the legacy source above and would otherwise appear twice.
      const guestThreads = (guestRes.ok && Array.isArray(guestJson?.data) ? guestJson.data : [])
        .filter((t) => !t.userId && t.deviceId)
        .map((t) => ({
          isGuest: true,
          threadKey: `d:${t.deviceId}`,
          inquiryId: currentEvent.id, // flat model: the thread id is the eventId
          deviceId: t.deviceId,
          firstName: t.firstName,
          lastName: t.lastName,
          messageCount: t.messageCount,
          lastMessage: t.lastMessage,
          lastMessageAt: t.lastMessageAt,
        }));

      setInquiryThreads(
        [...legacyThreads, ...guestThreads].sort(
          (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt),
        ),
      );
    } catch (error) {
      console.error("Fetch inquiries error:", error);
      setInquiryThreads([]);
    } finally {
      setIsLoadingInquiries(false);
      setInquiriesFetched(true);
    }
  };

  // Both sources are normalized to the render's { id, message, isMine, createdAt } shape.
  const fetchConversation = async (item) => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    setIsLoadingThread(true);
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    try {
      let messages = [];
      if (item.isGuest) {
        // Guest thread: event-inquiries, keyed by eventId + deviceId.
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.EVENT_INQUIRIES}/${item.inquiryId}/guest-conversation/${item.deviceId}`,
          { headers },
        );
        const result = await response.json().catch(() => null);
        const data = response.ok && Array.isArray(result?.data) ? result.data : [];
        messages = data.map((m) => ({
          id: m.id,
          message: m.inquiryMessage,
          isMine: m.ownerInquiry, // owner (this nonprofit) messages render on the right
          createdAt: m.createdAt,
        }));
      } else {
        // Logged-in thread: legacy inquiries, already { id, message, isMine, createdAt }.
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.INQUIRIES}/${item.inquiryId}/messages`,
          { headers },
        );
        const result = await response.json().catch(() => null);
        messages = response.ok && Array.isArray(result?.data) ? result.data : [];
      }
      setThread(messages);
    } catch (error) {
      console.error("Fetch conversation error:", error);
      setThread([]);
    } finally {
      setIsLoadingThread(false);
    }
  };

  const handleSelectInquiry = (item) => {
    setSelectedInquiry({
      isGuest: item.isGuest,
      threadKey: item.threadKey,
      inquiryId: item.inquiryId,
      deviceId: item.deviceId,
      name: inquiryDisplayName(item),
    });
    fetchConversation(item);
  };

  const handleBack = () => {
    setSelectedInquiry(null);
    setThread([]);
    setReplyMessage("");
  };

  const handleToggleInquiries = () => {
    const next = !showInquiries;
    setShowInquiries(next);
    if (!next) {
      setSelectedInquiry(null);
      setThread([]);
      setReplyMessage("");
    }
    if (next && !inquiriesFetched) {
      fetchInquiries();
    }
  };

  const handleReloadInquiries = (e) => {
    e.stopPropagation();
    fetchInquiries();
    if (selectedInquiry) {
      fetchConversation(selectedInquiry);
    }
  };

  const formatInquiryTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedInquiry) return;

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Please login to reply");
      return;
    }

    setIsSendingReply(true);
    const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
    try {
      // Guest threads: owner replies through the event-inquiries guest-conversation
      // route (authenticated → owner message). Logged-in threads: legacy messages
      // endpoint. Normalize both replies to the render shape.
      let response;
      let appended = null;
      if (selectedInquiry.isGuest) {
        response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.EVENT_INQUIRIES}/${selectedInquiry.inquiryId}/guest-conversation/${selectedInquiry.deviceId}`,
          { method: "POST", headers, body: JSON.stringify({ inquiryMessage: replyMessage.trim() }) },
        );
        const result = await response.json().catch(() => null);
        if (response.ok && result?.data) {
          appended = { id: result.data.id, message: result.data.inquiryMessage, isMine: true, createdAt: result.data.createdAt };
        }
      } else {
        response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.INQUIRIES}/${selectedInquiry.inquiryId}/messages`,
          { method: "POST", headers, body: JSON.stringify({ message: replyMessage.trim() }) },
        );
        const result = await response.json().catch(() => null);
        if (response.ok && result?.data) {
          appended = result.data; // already { message, isMine, createdAt }
        }
      }

      if (appended) {
        setThread((prev) => [...prev, appended]);
        setReplyMessage("");
        // Update message count and last message in the thread list
        setInquiryThreads((prev) =>
          prev.map((t) =>
            t.threadKey === selectedInquiry.threadKey
              ? { ...t, messageCount: t.messageCount + 1, lastMessage: replyMessage.trim(), lastMessageAt: appended.createdAt }
              : t,
          ),
        );
      } else {
        toast.error("Failed to send reply");
      }
    } catch (error) {
      console.error("Send reply error:", error);
      toast.error("Failed to send reply");
    } finally {
      setIsSendingReply(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "bg-[#3FE36B]";
      case "REJECTED":
        return "bg-red-500";
      case "PENDING":
        return "bg-[#DFCD00]";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "Accepted";
      case "REJECTED":
        return "Rejected";
      case "PENDING":
        return "Pending";
      default:
        return status || "Pending";
    }
  };

  // Helper function to get display name for role type
  const getRoleNameDisplay = (roleName) => {
    const displayMap = categories.reduce((acc, category) => {
      acc[category.value] = category.name;
      return acc;
    }, {});
    return displayMap[roleName] || "Other";
  };

  // const currentEvent = [
  //   {
  //     id: 1,
  //     title: "title",
  //     typeName: "typeName",
  //     description: "description",
  //     date: "25-10-2025",
  //     time: "10: 20",
  //     location: "location",
  //     needVolunteers: "needVolunteers",
  //     volunteerRoles: [
  //       {
  //         id: 1,
  //         roleName: "roleName",
  //         countNeeded: "countNeeded",
  //         specialRequirements: "specialRequirements",
  //       },
  //     ],
  //     attachments: [
  //       {
  //         id: 1,
  //         name: "name",
  //         url: "url",
  //       },
  //     ],
  //     resourceRequirements: "resourceRequirements",
  //     inviteNgoMunicipality: "inviteNgoMunicipality",
  //   },
  // ];

  if (!currentEvent) {
    return (
      <div className="w-[90%] lg:w-[95%] mx-auto my-12">
        <Link
          to="/dashboard/manage-events"
          className="inline-flex items-center gap-2 text-themeColor font-popinsMedium hover:underline"
        >
          <ChevronLeft size={20} />
          Back to Manage Events
        </Link>
        <div className="mt-12 text-center">
          <p className="font-popinsMedium text-lg text-black/60">
            We couldn't find the event details.
          </p>
        </div>
      </div>
    );
  }

  const volunteerRoles = currentEvent.roles ?? [];
  const attachments = currentEvent.attachments ?? [];

  return (
    <div className="w-[90%] lg:w-[95%] mx-auto my-8">
      <div className="flex flex-col lg:flex-row lg:justify-between gap-8">
        <div className="w-full lg:w-[64%]">
          <div className="">
            <h1 className="font-popinsBold text-xl lg:text-2xl">
              {currentEvent.title}
            </h1>
            <Link to={"/dashboard/manage-events"}>
              <button
                className="relative overflow-hidden py-1 px-3 border border-gray-300 text-gray-500 font-popinsRegular text-xs rounded
                             transform transition duration-300 ease-in-out
                             hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50
                             active:scale-95 focus:outline-none
                             flex items-center gap-x-1.5 mt-4"
              >
                <ChevronLeft size={12} />
                <span>Back to Manage Event</span>
              </button>
            </Link>
          </div>

          <section className="mt-8 space-y-5">
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <div className="lg:w-[50%]">
                <p className="font-popinsMedium text-base">Event Title</p>
                <p className="font-popinsRegular text-sm text-black/60 mt-1">
                  {currentEvent.title || "N/A"}
                </p>
              </div>
              <div className="lg:w-[50%]">
                <p className="font-popinsMedium text-base">Category</p>
                <p className="font-popinsRegular text-sm text-black/60 mt-1">
                  {currentEvent.typeName || currentEvent.categoryValue || "N/A"}
                </p>
              </div>
            </div>
            <div>
              <p className="font-popinsMedium text-base">Description</p>
              <p className="font-popinsRegular text-sm text-black/60 lg:w-[80%] mt-1">
                {currentEvent.description || "No description provided."}
              </p>
            </div>
            <div className="flex flex-col lg:flex-row lg:justify-between">
              <div className="w-[22%]">
                <p className="font-popinsMedium text-base">Date</p>
                <div className="flex items-center justify-between">
                  <p className="font-popinsRegular text-sm text-black/60 mt-1">
                    {currentEvent.date ? `${currentEvent.date.slice(5,7)}/${currentEvent.date.slice(8,10)}/${currentEvent.date.slice(0,4)}` : "N/A"}
                  </p>
                  <CalendarDays size={15} className="text-black/60" />
                </div>
              </div>
              <div className="w-[22%]">
                <p className="font-popinsMedium text-base">Time</p>
                <div className="flex items-center justify-between">
                  <p className="font-popinsRegular text-sm text-black/60 mt-1">
                    {currentEvent.time || "N/A"}
                  </p>
                  <Clock4 size={15} className="text-black/60" />
                </div>
              </div>
              <div className="w-[22%]">
                <p className="font-popinsMedium text-base">Location</p>
                <div className="flex items-center justify-between">
                  <p className="font-popinsRegular text-sm text-black/60 mt-1 break-words">
                    {currentEvent.location || "N/A"}
                  </p>
                  <MapPin size={15} className="text-black/60 flex-shrink-0 ml-1" />
                </div>
              </div>
              <div className="w-[22%]">
                <p className="font-popinsMedium text-base">ZIP Code</p>
                <p className="font-popinsRegular text-sm text-black/60 mt-1">
                  {currentEvent.zipCode || "N/A"}
                </p>
              </div>
            </div>
          </section>

          <section className="mt-8">
            <div className="mt-0">
              <h3 className="text-lg font-popinsSemiBold mb-2">Resources</h3>

              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="flex border-b border-gray-200 bg-gray-50 px-5 py-3.5">
                  <div className="w-[20%]">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Type
                    </p>
                  </div>
                  <div className="w-[20%]">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Quantity
                    </p>
                  </div>
                  <div className="w-[60%]">
                    <p className="font-popinsSemiBold text-sm text-gray-700">
                      Special Requirements
                    </p>
                  </div>
                </div>
                {volunteerRoles.length === 0 ? (
                  <div className="py-8 text-center text-gray-400 font-popinsRegular text-sm">
                    No volunteer roles were added for this event.
                  </div>
                ) : (
                  volunteerRoles.map((role, index) => (
                    <div
                      key={role.id || role.roleName}
                      className={`flex border-b last:border-b-0 border-gray-100 text-gray-700 py-4 px-5 items-center hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      <div className="w-[20%]">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {getRoleNameDisplay(role.roleName)}
                        </p>
                      </div>
                      <div className="w-[20%]">
                        <p className="font-popinsMedium text-sm text-gray-900">
                          {role.countNeeded}{" "}
                          {role.quantityType === "Person"
                            ? "Person(s)"
                            : "Unit(s)"}
                        </p>
                      </div>
                      <div className="w-[60%]">
                        <p className="font-popinsRegular text-sm text-gray-600 text-left break-words">
                          {role.specialRequirements ? (
                            role.specialRequirements
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="mt-8 space-y-5">
            <div>
              <p className="font-popinsMedium text-base">Supporting Files</p>
              {attachments.length === 0 ? (
                <p className="font-popinsRegular text-sm text-black/60 mt-1">
                  No supporting files uploaded.
                </p>
              ) : (
                <div className="mt-2 space-y-2">
                  {attachments.map((file, index) => {
                    // Normalize attachment to object format
                    let fileObj = file;
                    if (typeof file === "string") {
                      // If it's just a URL string, create object
                      const fileName =
                        file.split("/").pop() || `file-${index + 1}`;
                      fileObj = { url: file, name: fileName };
                    } else if (file && typeof file === "object") {
                      // Ensure it has url and name
                      fileObj = {
                        url: file.url || file,
                        name:
                          file.name ||
                          file.url?.split("/").pop() ||
                          `file-${index + 1}`,
                        id: file.id,
                      };
                    }

                    // Extract filename from URL if name is still a URL
                    let displayName = fileObj.name;
                    if (displayName && displayName.startsWith("http")) {
                      displayName =
                        displayName.split("/").pop() || `file-${index + 1}`;
                    }
                    // Clean up filename (remove query params, etc.)
                    displayName = displayName.split("?")[0];

                    // Determine file icon component based on file type
                    const getFileIcon = (fileName) => {
                      const ext = fileName.split(".").pop()?.toLowerCase();
                      if (ext === "pdf") {
                        return <FileText size={18} className="text-red-600" />;
                      }
                      if (["doc", "docx"].includes(ext)) {
                        return <FileText size={18} className="text-blue-600" />;
                      }
                      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
                        return (
                          <ImageIcon size={18} className="text-green-600" />
                        );
                      }
                      return <FileIcon size={18} className="text-gray-600" />;
                    };

                    const handleDownload = async (e) => {
                      e.preventDefault();
                      try {
                        const response = await fetch(fileObj.url);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = displayName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error("Error downloading file:", error);
                        toast.error(
                          "Failed to download file. Please try again.",
                        );
                      }
                    };

                    return (
                      <div
                        key={fileObj.id || fileObj.url || index}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        <div className="flex-shrink-0">
                          {getFileIcon(displayName)}
                        </div>
                        <span
                          className="text-sm text-gray-700 flex-1 truncate font-popinsRegular"
                          title={displayName}
                        >
                          {displayName}
                        </span>
                        <button
                          onClick={handleDownload}
                          className="flex items-center gap-2 px-3 py-1.5 bg-themeColor text-white rounded-md hover:bg-[#3FC4E3] transition-colors text-sm font-popinsMedium disabled:opacity-50 disabled:cursor-not-allowed"
                          title={`Download ${displayName}`}
                        >
                          <Download size={16} />
                          <span>Download</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <p className="font-popinsMedium text-base">
                Resource Requirements
              </p>
              <p className="font-popinsRegular text-sm text-black/60 mt-1">
                {currentEvent.resourceRequirements ||
                  "No additional resources requested."}
              </p>
            </div>
            <div>
              <p className="font-popinsMedium text-base">Co-hosting</p>
              <p className="font-popinsRegular text-sm text-black/60 mt-1">
                {currentEvent.inviteNgoMunicipality
                  ? "Partner NGO invited."
                  : "No co-hosts invited."}
              </p>
            </div>
          </section>

          {/* Collaboration Requests as Tasks */}
          <section className="mt-8">
            <h3 className="text-lg font-popinsSemiBold mb-5">
              Collaboration Requests
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-300 panelScroll">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Resource
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Assign To
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-popinsRegular">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingRequests ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm"
                      >
                        Loading...
                      </td>
                    </tr>
                  ) : collaborationRequests.length === 0 ? (
                    <tr>
                      <td
                        colSpan="4"
                        className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm"
                      >
                        No collaboration requests sent for this event.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Show collaboration requests as tasks */}
                      {collaborationRequests
                        .map((recipient) => {
                          // If recipient has tasks, show each task as a row
                          // Otherwise, show the collaboration request itself as a row
                          const tasks = recipient.tasks || [];

                          if (tasks.length > 0) {
                            // Show each task with the recipient's status
                            return tasks.map((task) => (
                              <tr
                                className="border-t"
                                key={`${recipient.id}-${task.id}`}
                              >
                                <td className="px-4 py-4 text-sm font-popinsRegular">
                                  {task.taskName}
                                </td>
                                <td className="px-4 py-4 text-sm font-popinsRegular">
                                  {task.countNeeded || "—"}
                                </td>
                                <td className="px-4 py-4 text-sm font-popinsRegular text-black/60">
                                  {recipient.nonprofit?.organizationName ||
                                    "N/A"}
                                </td>
                                <td className="px-4 py-4 font-popinsRegular">
                                  <div
                                    className={`${getStatusColor(
                                      recipient.status,
                                    )} flex justify-center rounded-lg px-7 py-2 font-popinsMedium text-sm text-white`}
                                  >
                                    {getStatusText(recipient.status)}
                                  </div>
                                </td>
                              </tr>
                            ));
                          } else {
                            // No tasks, show the collaboration request itself
                            return (
                              <tr className="border-t" key={recipient.id}>
                                <td className="px-4 py-4 text-sm font-popinsRegular">
                                  Collaboration Request
                                </td>
                                <td className="px-4 py-4 text-sm font-popinsRegular">
                                  —
                                </td>
                                <td className="px-4 py-4 text-sm font-popinsRegular text-black/60">
                                  {recipient.nonprofit?.organizationName ||
                                    "N/A"}
                                </td>
                                <td className="px-4 py-4 font-popinsRegular">
                                  <div
                                    className={`${getStatusColor(
                                      recipient.status,
                                    )} flex justify-center rounded-lg px-7 py-2 font-popinsMedium text-sm text-white`}
                                  >
                                    {getStatusText(recipient.status)}
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        })
                        .flat()}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Inquiries Section - Toggleable */}
          <section className="mt-8">
            <button
              type="button"
              onClick={handleToggleInquiries}
              className="w-full flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MessageCircle size={20} className="text-[#3FC4E3]" />
                <h3 className="text-lg font-popinsSemiBold">Inquiries</h3>
                {inquiryThreads.length > 0 && (
                  <span className="bg-[#3FC4E3] text-white text-xs font-popinsMedium rounded-full px-2 py-0.5">
                    {inquiryThreads.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {showInquiries && (
                  <button
                    type="button"
                    onClick={handleReloadInquiries}
                    disabled={isLoadingInquiries}
                    title="Refresh inquiries"
                    className="p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RefreshCw
                      size={15}
                      className={`text-black/50 ${isLoadingInquiries ? "animate-spin" : ""}`}
                    />
                  </button>
                )}
                <ChevronDown
                  size={20}
                  className={`text-black/50 transition-transform ${
                    showInquiries ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {showInquiries && (
              <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                {isLoadingInquiries ? (
                  <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                    Loading inquiries...
                  </div>
                ) : selectedInquiry ? (
                  /* ── Thread view ── */
                  <>
                    {/* Thread header */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50">
                      <button
                        type="button"
                        onClick={handleBack}
                        className="p-1 rounded hover:bg-gray-200 transition-colors"
                      >
                        <ChevronLeft size={18} className="text-black/60" />
                      </button>
                      <span className="font-popinsMedium text-sm text-black/80">
                        {selectedInquiry.name}
                      </span>
                    </div>

                    {/* Messages */}
                    {isLoadingThread ? (
                      <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                        Loading conversation...
                      </div>
                    ) : (
                      <div className="max-h-[240px] overflow-y-auto p-4 space-y-2">
                        {thread.length === 0 ? (
                          <p className="text-center text-black/40 font-popinsRegular text-sm">No messages yet.</p>
                        ) : (
                          thread.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.isMine ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                                  msg.isMine
                                    ? "bg-[#3FC4E3] text-white rounded-br-sm"
                                    : "bg-gray-100 text-black/80 rounded-bl-sm"
                                }`}
                              >
                                <p className="font-popinsRegular text-sm">{msg.message}</p>
                                <p
                                  className={`font-popinsRegular text-[10px] mt-0.5 ${
                                    msg.isMine ? "text-white/70 text-right" : "text-black/40"
                                  }`}
                                >
                                  {formatInquiryTime(msg.createdAt)}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Reply input */}
                    <div className="flex items-center gap-2 p-3 border-t border-gray-200">
                      <input
                        type="text"
                        placeholder="Type a reply..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                        }}
                        disabled={isSendingReply}
                        className="flex-1 font-popinsRegular text-sm rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] transition-all"
                      />
                      <button
                        type="button"
                        onClick={handleSendReply}
                        disabled={isSendingReply || !replyMessage.trim()}
                        className="p-2 rounded-lg bg-[#3FC4E3] text-white hover:bg-[#35b0cc] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  </>
                ) : inquiryThreads.length === 0 ? (
                  <div className="px-4 py-6 text-center text-black/50 font-popinsRegular text-sm">
                    No inquiries yet.
                  </div>
                ) : (
                  /* ── Inquiry thread list ── */
                  <div className="divide-y divide-gray-100">
                    {inquiryThreads.map((item) => (
                      <button
                        key={item.threadKey}
                        type="button"
                        onClick={() => handleSelectInquiry(item)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-[#3FC4E3]/15 flex items-center justify-center flex-shrink-0">
                            <span className="text-[#3FC4E3] font-popinsMedium text-xs">
                              {(inquiryDisplayName(item)[0] || "?").toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-popinsMedium text-sm text-black/80 truncate">
                              {inquiryDisplayName(item)}
                            </p>
                            <p className="font-popinsRegular text-xs text-black/40 truncate">
                              {item.lastMessage}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                          <span className="font-popinsRegular text-[10px] text-black/40">
                            {formatInquiryTime(item.lastMessageAt)}
                          </span>
                          <span className="bg-[#3FC4E3] text-white text-[10px] font-popinsMedium rounded-full px-1.5 py-0.5">
                            {item.messageCount}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
        <div className="w-full lg:w-[34%] hidden lg:block space-y-5">
          <div className="">
            <h3 className="font-popinsSemiBold text-xl mb-2">Cover photo</h3>
            {currentEvent?.coverImageUrl ? (
              <div className="relative">
                {isImageLoading && (
                  <div className="absolute inset-0 rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer"></div>
                  </div>
                )}
                <img
                  ref={imageRef}
                  src={currentEvent.coverImageUrl}
                  alt={currentEvent.title || "Event cover"}
                  className={`w-full h-[200px] object-cover rounded-lg ${
                    isImageLoading ? "opacity-0" : "opacity-100"
                  } transition-opacity duration-300`}
                  onLoad={() => {
                    setIsImageLoading(false);
                    setImageError(false);
                  }}
                  onError={() => {
                    setIsImageLoading(false);
                    setImageError(true);
                  }}
                />
                {imageError && (
                  <div className="w-full h-[200px] bg-red-50 border-2 border-red-300 rounded-lg flex items-center justify-center">
                    <p className="text-red-500 text-sm">Image failed to load</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-[200px] bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-400 text-sm">No cover image</p>
              </div>
            )}
          </div>

          {/* Map embed */}
          {currentEvent.latitude && currentEvent.longitude && (
            <div>
              <h3 className="font-popinsSemiBold text-xl mb-2">Location Map</h3>
              <MapContainer
                center={[currentEvent.latitude, currentEvent.longitude]}
                zoom={15}
                style={{ height: "200px", width: "100%", borderRadius: "8px" }}
                scrollWheelZoom={false}
                dragging={false}
                zoomControl={false}
                doubleClickZoom={false}
                attributionControl={false}
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                  subdomains="abcd"
                  maxZoom={19}
                />
                <Marker position={[currentEvent.latitude, currentEvent.longitude]} />
              </MapContainer>
            </div>
          )}

          {/* Impressions & Clicks */}
          <div className="rounded-lg border border-gray-200 p-4">
            <h3 className="font-popinsSemiBold text-base mb-3">Event Stats</h3>
            <div className="flex gap-4">
              <div className="flex-1 flex items-center gap-3 bg-[#3FC4E3]/10 rounded-lg px-3 py-3">
                <Eye size={18} className="text-[#3FC4E3] flex-shrink-0" />
                <div>
                  <p className="font-popinsBold text-lg leading-none">{currentEvent.viewCount ?? 0}</p>
                  <p className="font-popinsRegular text-xs text-black/50 mt-0.5">Views</p>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-3 bg-themeColor/10 rounded-lg px-3 py-3">
                <MousePointerClick size={18} className="text-themeColor flex-shrink-0" />
                <div>
                  <p className="font-popinsBold text-lg leading-none">{currentEvent.clickCount ?? 0}</p>
                  <p className="font-popinsRegular text-xs text-black/50 mt-0.5">Clicks</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
